import { useLazyQuery } from '@vue/apollo-composable';
import { useConstructionDocumentationStore } from 'store/construction-documentation';
import { computed } from 'vue';

import { useDict, useFischer } from '@/composables/fischerPlugin';
import { useUser } from '@/composables/user';
import GET_CONSTRUCTION_AREA from '@/graphql/getConstructionArea.gql';
import GET_CONSTRUCTION_AREA_LIST from '@/graphql/getConstructionAreaList.gql';
import GET_CONSTRUCTION_FIRESTOP from '@/graphql/getConstructionFirestop.gql';
import GET_CONSTRUCTION_FIRESTOP_COMMENTS from '@/graphql/getConstructionFirestopComments.gql';
import GET_CONSTRUCTION_FIRESTOP_SUMMARY from '@/graphql/getConstructionFirestopSummary.gql';
import GET_CONSTRUCTION_FIRESTOP_TEMPLATE from '@/graphql/getConstructionFirestopTemplate.gql';
import GET_CONSTRUCTION_ROOM from '@/graphql/getConstructionRoom.gql';
import GET_CONSTRUCTION_ROOM_LIST from '@/graphql/getConstructionRoomList.gql';
import GET_CONSTRUCTION_SITE from '@/graphql/getConstructionSite.gql';

export const useConstructionDocumentation = () => {
  const projectType = 'cd';
  const fischer = useFischer();
  const dict = useDict();
  const cdStore = useConstructionDocumentationStore();
  const { logoutIfTokenInvalid } = useUser();

  const pageUrl = fischer.pages.constructionDocumentation()?.url;

  const setBrowserPageTitle = (pageTitle: string | undefined) => {
    if (IS_SSR) {
      return;
    }

    const title = document.querySelector('head title');
    if (title) {
      if (pageTitle) {
        title.textContent = `${pageTitle} - ${dict.get('construction-Projects.Breadcrumb.Construction-Documentation')} | ${fischer.brandName()}`;
      } else {
        title.textContent = `${dict.get('construction-Projects.Breadcrumb.Construction-Documentation')} | ${fischer.brandName()}`;
      }
    }
  };

  const formatNameForUrl = (name: string) => {
    return name
      .replace(/[^A-Za-z0-9\s]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+$/, '')
      .toLowerCase();
  };

  const getPathString = (pageType: string) => {
    const data: any = cdStore[pageType as keyof typeof cdStore];
    if (data) {
      const name = formatNameForUrl(data?.name);
      const id = cdStore[`${pageType}Id` as keyof typeof cdStore];
      if (typeof id !== 'string') {
        console.error('missing id for pageType', pageType);
        return '';
      }
      return encodeURI(`/${name}_${id.replace(/.*:/, '')}`);
    }
    return '';
  };

  const setTabId = (tabId: string) => {
    return new Promise<void>((resolve, reject) => {
      try {
        window.localStorage.setItem('cdInitTab', tabId);
        resolve();
      } catch (err) {
        console.error('Could not set construction init tab id', err);
        reject();
      }
    });
  };

  const getCDUrl = (
    targetName?: string | null,
    targetId?: string | null,
    targetType?: string | null,
  ) => {
    if (!targetId || !targetName) {
      return '';
    }

    const name = formatNameForUrl(targetName);
    const host = cdStore.isArchive ? pageUrl + '/archive' : pageUrl;
    const id = targetId.replace(/.*:/, '');

    if (targetType === 'area') {
      return host + getPathString('project') + encodeURI(`/${name}_${id}`);
    }

    if (targetType === 'room') {
      return host + getPathString('project') + getPathString('area') + encodeURI(`/${name}_${id}`);
    }

    if (targetType === 'firestop') {
      if (cdStore.room) {
        return (
          host +
          getPathString('project') +
          getPathString('area') +
          getPathString('room') +
          encodeURI(`/${name}_${id}`)
        );
      }

      return host + getPathString('project') + getPathString('area') + encodeURI(`/${name}_${id}`);
    }

    return host + encodeURI(`/${name}_${targetId}`);
  };

  const graphQlVariables = computed(() => {
    return {
      siteId: cdStore.projectId,
      areaId: cdStore.areaId,
      roomId: cdStore.roomId,
      documentId: cdStore.firestopId,
      impersonatedUser: fischer.user.getAssistedServiceEmail(),
      accessToken: fischer.user.accessToken(),
      language: fischer.siteLanguage(),
      project: projectType,
      templateVersion: cdStore.templateVersion,
    };
  });

  const {
    load: loadProject,
    onError: onProjectError,
    refetch: doRefetchProject,
    loading: projectLoading,
  } = useLazyQuery(GET_CONSTRUCTION_SITE, graphQlVariables.value);

  onProjectError((err: any) => {
    console.error('could not load construction documentation project', err);

    const { graphQLErrors } = err;
    if (graphQLErrors.some((e: any) => e.extensions.code === 'NOT_PERMITTED')) {
      window.location.href = fischer.pages.constructionDocumentation().url;
      return;
    } else if (graphQLErrors.some((e: any) => e.extensions.code === 'PROCESSING_AGREEMENT')) {
      constructionConsentDialog.value?.open();
    } else if (graphQLErrors.some((e: any) => e.extensions.code === 'NOT_AUTHORIZED')) {
      userNotAuthorized.value = true;
    }

    logoutIfTokenInvalid(err);
  });

  const {
    load: loadArea,
    onError: onAreaError,
    refetch: doRefetchArea,
    loading: areaLoading,
  } = useLazyQuery(GET_CONSTRUCTION_AREA, graphQlVariables.value);

  onAreaError((err: any) => {
    console.error('could not load construction documentation area', err);
  });

  const {
    load: loadAreaList,
    onError: onAreaListError,
    refetch: doRefetchAreaList,
    loading: areaListLoading,
  } = useLazyQuery(GET_CONSTRUCTION_AREA_LIST, graphQlVariables.value);

  onAreaListError((err: any) => {
    console.error('could not load construction documentation area list', err);
  });

  const {
    load: loadRoom,
    onError: onRoomError,
    refetch: doRefetchRoom,
    loading: roomLoading,
  } = useLazyQuery(GET_CONSTRUCTION_ROOM, graphQlVariables.value);

  onRoomError((err: any) => {
    console.error('could not load construction documentation room', err);
  });

  const {
    load: loadFirestop,
    onError: onFirestopError,
    refetch: doRefetchFirestop,
    loading: firestopLoading,
  } = useLazyQuery(GET_CONSTRUCTION_FIRESTOP, graphQlVariables.value);

  onFirestopError((err: any) => {
    console.error('could not load construction documentation firestop', err);
  });

  const { load: loadFirestopTemplate, onError: onFirestopTemplateError } = useLazyQuery(
    GET_CONSTRUCTION_FIRESTOP_TEMPLATE,
    graphQlVariables.value,
  );

  onFirestopTemplateError((err: any) => {
    console.error('could not load construction documentation firestop template', err);
  });

  const {
    load: loadFirestopComments,
    onError: onFirestopCommentsError,
    refetch: doRefetchFirestopComments,
    loading: firestopCommentsLoading,
  } = useLazyQuery(GET_CONSTRUCTION_FIRESTOP_COMMENTS, graphQlVariables.value);

  onFirestopCommentsError((err: any) => {
    console.error('could not load construction documentation firestop comments', err);
  });

  const {
    load: loadRoomList,
    onError: onRoomListError,
    refetch: doRefetchRoomList,
    loading: roomListLoading,
  } = useLazyQuery(GET_CONSTRUCTION_ROOM_LIST, graphQlVariables.value);

  onRoomListError((err: any) => {
    console.error('could not load construction documentation room list', err);
  });

  const {
    load: loadFirestops,
    onError: onFirestopSummaryError,
    refetch: refetchFirestops,
    loading: firestopSummaryLoading,
  } = useLazyQuery(GET_CONSTRUCTION_FIRESTOP_SUMMARY, graphQlVariables.value);

  onFirestopSummaryError((err: any) => {
    console.error('could not load construction documentation firestop summary', err);
  });

  const loadConstructionDocumentationData = async () => {
    const promises = [];

    if (cdStore.projectId) {
      promises.push(loadProject());
      promises.push(loadAreaList());
    } else {
      promises.push(null, null);
    }

    if (cdStore.areaId) {
      promises.push(loadArea());
      promises.push(loadRoomList());
      promises.push(loadFirestops());
    } else {
      promises.push(null, null, null);
    }

    if (cdStore.roomId) {
      promises.push(loadRoom());
    } else {
      promises.push(null);
    }

    if (cdStore.isAddDocumentationPointPage) {
      promises.push(loadFirestopTemplate());
    } else {
      promises.push(null);
    }

    if (cdStore.firestopId) {
      promises.push(loadFirestop());
      promises.push(loadFirestopComments());
    } else {
      promises.push(null, null);
    }

    if (!promises.length) {
      return;
    }

    try {
      cdStore.isLoading = true;
      const [
        project,
        areaList,
        area,
        roomList,
        firestopSummary,
        room,
        firestopTemplate,
        firestop,
        firestopComments,
      ] = await Promise.all(promises);

      if (areaList?.constructionAreaList?.areas) {
        cdStore.project = {
          ...project?.constructionSite,
          areas: areaList.constructionAreaList.areas,
        };
      } else {
        cdStore.project = project?.constructionSite;
      }

      if (roomList?.constructionRoomList?.rooms) {
        cdStore.area = {
          ...area?.constructionArea,
          rooms: roomList.constructionRoomList.rooms,
        };
      } else {
        cdStore.area = area?.constructionArea;
      }
      cdStore.firestopSummary = firestopSummary?.firestopSummary?.items;
      cdStore.room = room?.constructionRoom;
      cdStore.firestopTemplate = firestopTemplate?.constructionFirestopTemplate?.sections;
      if (firestopComments?.constructionFirestopComments) {
        cdStore.firestop = {
          ...firestop?.constructionFirestop,
          comments: firestopComments?.constructionFirestopComments?.sections || [],
        };
      } else {
        cdStore.firestop = firestop?.constructionFirestop;
      }
      cdStore.isArchive = project?.constructionSite?.archived;
    } catch (err) {
      console.error('could not fetch construction documentation data', err);
    } finally {
      cdStore.isLoading = false;
    }
  };

  const getCDBreadcrumb = computed(() => {
    if (!cdStore || IS_SSR || !window.location.href.includes(pageUrl)) {
      return null;
    }

    const breadcrumbs = [];
    const host = cdStore.isArchive ? pageUrl + '/archive' : pageUrl;

    if (IS_SHOWROOM && window.location.href.includes(pageUrl)) {
      breadcrumbs.push({
        url: pageUrl,
        title:
          dict.get('construction-Projects.Breadcrumb.Construction-Documentation') ||
          'Construction Documentation',
      });
    }

    if (cdStore.isArchive) {
      breadcrumbs.push({
        url: host,
        title: dict.get('construction-Projects.Breadcrumb.Archive') || 'Archive',
      });
    }

    if (cdStore.project) {
      breadcrumbs.push({
        url: host + getPathString('project'),
        title: cdStore.project.name,
      });
    }

    if (cdStore.area) {
      breadcrumbs.push({
        url: host + getPathString('project') + getPathString('area'),
        title: cdStore.area.name,
      });
    }

    if (cdStore.room) {
      breadcrumbs.push({
        url: host + getPathString('project') + getPathString('area') + getPathString('room'),
        title: cdStore.room.name,
      });
    }

    if (cdStore.firestop) {
      breadcrumbs.push({
        url:
          host +
          getPathString('project') +
          getPathString('area') +
          getPathString('room') +
          getPathString('firestop'),
        title: cdStore.firestop.name,
      });
    }

    if (cdStore.isAddDocumentationPointPage && !cdStore.isLoading) {
      // url can be empty because always last element
      breadcrumbs.push({
        url: '',
        title:
          dict.get('construction-Projects.Breadcrumb.Add-Documentation-Point') ||
          'Add Documentation Point',
      });
    }

    if (cdStore.isAuditLogPage && !cdStore.isLoading) {
      // url can be empty because always last element
      breadcrumbs.push({
        url: '',
        title: dict.get('construction-Projects.Breadcrumb.Audit-Log') || 'Audit Log',
      });
    }

    if (cdStore.isReportsPage && !cdStore.isLoading) {
      // url can be empty because always last element
      breadcrumbs.push({
        url: '',
        title: dict.get('construction-Projects.Breadcrumb.Reports') || 'Reports',
      });
    }

    if (cdStore.isEJDocumentsPage && !cdStore.isLoading) {
      // url can be empty because always last element
      breadcrumbs.push({
        url: '',
        title: dict.get('construction-Projects.Breadcrumb.EJ-Documents') || 'EJ Documents',
      });
    }

    if (cdStore.isThirdPartyProductsPage && !cdStore.isLoading) {
      // url can be empty because always last element
      breadcrumbs.push({
        url: '',
        title:
          dict.get('construction-Projects.Breadcrumb.Third-Party-Products') ||
          'Third Party Products',
      });
    }

    if (cdStore.isManageParticipantsPage && !cdStore.isLoading) {
      // url can be empty because always last element
      breadcrumbs.push({
        url: '',
        title:
          dict.get('construction-Projects.Breadcrumb.Manage-Participants') || 'Manage Participants',
      });
    }

    if (cdStore.isSearchPage && !cdStore.isLoading) {
      // url can be empty because always last element
      breadcrumbs.push({
        url: '',
        title: dict.get('construction-Projects.Breadcrumb.Search') || 'Search',
      });
    }

    return breadcrumbs;
  });

  const refetchProject = async () => {
    try {
      await loadProject();
      const result = await doRefetchProject();
      cdStore.project = { ...result?.data?.constructionSite, areas: cdStore.project?.areas };
    } catch (err) {
      console.error('could not fetch construction documentation project data', err);
    }
  };

  const refetchArea = async () => {
    try {
      await loadArea();
      const result = await doRefetchArea();
      cdStore.area = { ...result?.data?.constructionArea, rooms: cdStore.area?.rooms };
    } catch (err) {
      console.error('could not refetch construction documentation area', err);
    }
  };

  const refetchAreaList = async () => {
    try {
      await loadAreaList();
      const result = await doRefetchAreaList();
      if (cdStore.project && result?.data?.constructionAreaList?.areas) {
        cdStore.project.areas = result.data.constructionAreaList.areas;
      }
    } catch (err) {
      console.error('could not refetch area list', err);
    }
  };

  const refetchRoom = async () => {
    try {
      await loadRoom();
      const result = await doRefetchRoom();
      cdStore.room = result?.data?.constructionRoom;
    } catch (err) {
      console.error('could not refetch construction documentation area', err);
    }
  };

  const refetchRoomList = async () => {
    try {
      await loadRoomList();
      const result = await doRefetchRoomList();
      if (cdStore.area && result?.data?.constructionRoomList?.rooms) {
        cdStore.area.rooms = result.data.constructionRoomList.rooms;
      }
    } catch (err) {
      console.error('could not refetch room list', err);
    }
  };

  const refetchFirestop = async () => {
    try {
      await loadFirestop();
      const result = await doRefetchFirestop();
      cdStore.firestop = {
        ...result?.data?.constructionFirestop,
        comments: cdStore.firestop?.comments,
      };
    } catch (err) {
      console.error('could not refetch construction documentation firestop', err);
    }
  };

  const refetchFirestopComments = async () => {
    try {
      await loadFirestopComments();
      const result = await doRefetchFirestopComments();
      cdStore.firestop = {
        ...cdStore.firestop,
        comments: result?.data?.constructionFirestopComments?.sections,
      };
    } catch (err) {
      console.error('could not refetch construction documentation firestop', err);
    }
  };
  const refetchFirestopSummary = async () => {
    try {
      await loadFirestops();
      const result = await refetchFirestops();
      if (cdStore.firestopSummary && result?.data?.firestopSummary?.items) {
        cdStore.firestopSummary = result.data.firestopSummary.items;
      }
    } catch (err) {
      console.error('could not refetch firestop summary', err);
    }
  };

  return {
    pageUrl,
    setBrowserPageTitle,
    setTabId,
    refetchProject,
    projectLoading,
    refetchArea,
    areaLoading,
    refetchAreaList,
    areaListLoading,
    refetchRoom,
    roomLoading,
    refetchRoomList,
    roomListLoading,
    refetchFirestop,
    firestopLoading,
    refetchFirestopComments,
    firestopCommentsLoading,
    refetchFirestopSummary,
    firestopSummaryLoading,
    getCDUrl,
    formatNameForUrl,
    loadConstructionDocumentationData,
    getCDBreadcrumb,
  };
};
